import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { track } from '../lib/analytics';
import { CATEGORIES, CONDITIONS, PICKUP_METHODS } from '../lib/constants';
import { encodeGeohash } from '../lib/geo';
import { coverPhoto, formatPrice, listingStatusLabel } from '../lib/format';
import Empty from '../components/Empty';
import Icon from '../components/Icon';
import LocationPicker from '../components/LocationPicker';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EMPTY_FORM = {
  title: '',
  price: '',
  category: 'Furniture',
  condition: 'Good',
  description: '',
  shipping: false,
  free: false,
  quantity: 1,
  pickupMethod: PICKUP_METHODS[2],
};

function photosFromListing(listing) {
  const urls = Array.isArray(listing?.photos) ? listing.photos : [];
  return urls.filter(Boolean).map((url, i) => ({
    id: `existing-${i}-${url}`,
    url,
  }));
}

export default function SellPage({ user }) {
  const nav = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const creating = location.pathname.endsWith('/new') || params.get('create') === '1';
  const editing = Boolean(editId);
  const showForm = creating || editing;

  const [mine, setMine] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loadingEdit, setLoadingEdit] = useState(editing);
  const [existing, setExisting] = useState(null);
  const imageInput = useRef();
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pickup, setPickup] = useState(null);
  const [showLocation, setShowLocation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    const created = params.get('created') === '1';
    const updated = params.get('updated') === '1';
    if (!created && !updated) return;
    setToast(created ? 'Listing submitted for review' : 'Listing updated');
    nav('/sell', { replace: true });
  }, [params, nav]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'listings'),
      where('sellerId', '==', user.uid),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(
      q,
      (snap) => setMine(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setMine([]),
    );
  }, [user]);

  useEffect(() => {
    if (!editing || !user || !editId) {
      setLoadingEdit(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingEdit(true);
    setLoadError('');
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'listings', editId));
        if (cancelled) return;
        if (!snap.exists()) {
          setLoadError('This listing doesn’t exist.');
          setLoadingEdit(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        if (data.sellerId !== user.uid) {
          setLoadError('You can only edit your own listings.');
          setLoadingEdit(false);
          return;
        }
        setExisting(data);
        setForm({
          title: data.title || '',
          price: data.isFree ? '' : String(data.price ?? ''),
          category: data.category || 'Furniture',
          condition: data.condition || 'Good',
          description: data.description || '',
          shipping: data.supportsShipping === true,
          free: data.isFree === true,
          quantity: Number(data.quantity) || 1,
          pickupMethod: data.pickup?.pickupMethod || PICKUP_METHODS[2],
        });
        setPhotos(photosFromListing(data));
        if (data.pickup?.lat != null && data.pickup?.lng != null) {
          setPickup({
            label: data.pickup.label || [data.pickup.city, data.pickup.state].filter(Boolean).join(', '),
            city: data.pickup.city || '',
            state: data.pickup.state || '',
            lat: data.pickup.lat,
            lng: data.pickup.lng,
            geohash: data.pickup.geohash || '',
          });
        } else {
          setPickup(null);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Could not load listing.');
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, editId, user]);

  const stats = useMemo(() => {
    const totalViews = mine.reduce((sum, item) => sum + (Number(item.viewCount) || 0), 0);
    const totalLikes = mine.reduce((sum, item) => sum + (Number(item.likeCount) || 0), 0);
    return { totalViews, totalLikes, count: mine.length };
  }, [mine]);

  const set = (key, value) => setForm((x) => ({ ...x, [key]: value }));

  const resetCreate = () => {
    setPhotos([]);
    setForm(EMPTY_FORM);
    setPickup(null);
    setError('');
    setExisting(null);
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setPhotos((prev) => {
      const room = Math.max(0, 10 - prev.length);
      const next = incoming.slice(0, room).map((file, i) => ({
        id: `new-${Date.now()}-${i}-${file.name}`,
        file,
        preview: URL.createObjectURL(file),
      }));
      return [...prev, ...next];
    });
  };

  const removePhoto = (id) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const resolvePhotoUrls = async (listingId) => {
    const urls = [];
    for (const photo of photos) {
      if (photo.url) {
        urls.push(photo.url);
        continue;
      }
      if (!photo.file) continue;
      const base64 = await fileToBase64(photo.file);
      const upload = httpsCallable(functions, 'uploadListingImage');
      const result = await upload({
        listingId,
        contentType: photo.file.type || 'image/jpeg',
        extension: photo.file.name.split('.').pop() || 'jpg',
        base64: String(base64).split(',')[1],
      });
      urls.push(result.data.publicUrl);
    }
    return urls;
  };

  const buildPickupPayload = () => ({
    label: pickup.label,
    city: pickup.city || '',
    state: pickup.state || '',
    lat: pickup.lat,
    lng: pickup.lng,
    geohash: pickup.geohash || encodeGeohash(pickup.lat, pickup.lng),
    pickupMethod: form.pickupMethod,
  });

  const submit = async (e, asDraft = false) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Add a title to continue.');
    if (!form.free && form.price === '') return setError('Add a price or mark it free.');
    if (!pickup?.lat) return setError('Set a pickup area on the map.');
    if (!photos.length) return setError('Add at least one photo.');
    setBusy(true);
    setError('');
    try {
      const price = form.free ? 0 : Number(form.price);
      const quantity = Math.max(1, Math.floor(Number(form.quantity) || 1));
      const pickupPayload = buildPickupPayload();

      if (editing && existing) {
        const urls = await resolvePhotoUrls(existing.id);
        const mainPhotoIndex = 0;
        const title = form.title.trim();
        const description = form.description.trim();
        const needsReview =
          existing.status === 'approved' &&
          (existing.title !== title ||
            (existing.description || '') !== description ||
            JSON.stringify(existing.photos || []) !== JSON.stringify(urls) ||
            (existing.mainPhotoIndex || 0) !== mainPhotoIndex);
        const nextStatus = needsReview
          ? 'pending'
          : existing.status === 'approved'
            ? 'approved'
            : asDraft
              ? 'draft'
              : 'pending';

        const existingEffective = existing.isFree ? 0 : Number(existing.price) || 0;
        const nextEffective = form.free ? 0 : price;
        const priceChanged =
          existingEffective !== nextEffective || Boolean(existing.isFree) !== form.free;
        const isPriceDrop = priceChanged && nextEffective < existingEffective;
        const previousPrice = priceChanged
          ? existingEffective
          : existing.previousPrice ?? null;

        const updates = {
          title,
          description,
          price,
          isFree: form.free,
          previousPrice,
          supportsShipping: form.shipping,
          category: form.category,
          condition: form.condition,
          photos: urls,
          mainPhotoIndex,
          quantity,
          status: nextStatus,
          soldOutcome: '',
          rejectionReason: '',
          removedByAdmin: false,
          pickup: pickupPayload,
          updatedAt: serverTimestamp(),
          contentUpdatedAt: serverTimestamp(),
        };
        if (isPriceDrop) {
          updates.priceDroppedAt = serverTimestamp();
        } else if (priceChanged) {
          updates.priceDroppedAt = deleteField();
        }

        await updateDoc(doc(db, 'listings', existing.id), updates);
        nav('/sell?updated=1');
        return;
      }

      const ref = doc(collection(db, 'listings'));
      const urls = await resolvePhotoUrls(ref.id);
      await setDoc(ref, {
        sellerId: user.uid,
        title: form.title.trim(),
        description: form.description.trim(),
        price,
        isFree: form.free,
        previousPrice: null,
        supportsShipping: form.shipping,
        currency: 'USD',
        category: form.category,
        condition: form.condition,
        photos: urls,
        mainPhotoIndex: 0,
        quantity,
        status: asDraft ? 'draft' : 'pending',
        pickup: pickupPayload,
        soldOutcome: '',
        viewCount: 0,
        likeCount: 0,
        saveCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      track('listing_created', {
        item_id: ref.id,
        item_category: form.category || '',
        price,
        as_draft: asDraft,
      });
      resetCreate();
      nav('/sell?created=1');
    } catch (err) {
      setError(err.message || (editing ? 'We could not update this listing.' : 'We could not create this listing.'));
    } finally {
      setBusy(false);
    }
  };

  if (showForm) {
    if (editing && loadingEdit) {
      return (
        <section className="content-page sell-page">
          <div className="listing-skeleton tall" />
        </section>
      );
    }

    if (editing && loadError) {
      return (
        <section className="content-page sell-page">
          <Empty
            icon="tag"
            title="Can’t edit listing"
            body={loadError}
            action={
              <Link className="primary-button" to="/sell">
                Back to selling
              </Link>
            }
          />
        </section>
      );
    }

    return (
      <section className="content-page sell-page">
        <div className="page-title sell-create-head">
          <button type="button" className="back-link" onClick={() => nav('/sell')}>
            <Icon name="chevronLeft" size={18} /> Back
          </button>
          <h1>{editing ? 'Edit listing' : 'Add listing'}</h1>
          <p>
            {editing
              ? 'Update photos, details, or pickup. Big changes go back through review.'
              : 'Photos, details, and a pickup area. It goes live after a quick review.'}
          </p>
        </div>

        <form className="listing-form" onSubmit={(e) => submit(e, false)}>
          <div className="photo-drop-wrap">
            <label className="photo-drop" onClick={() => imageInput.current?.click()}>
              <input
                ref={imageInput}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              {photos.length ? (
                <div className="photo-previews">
                  {photos.map((photo) => (
                    <div key={photo.id} className="photo-preview-item">
                      <img src={photo.url || photo.preview} alt="" />
                      <button
                        type="button"
                        className="photo-remove"
                        aria-label="Remove photo"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          removePhoto(photo.id);
                        }}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <span className="camera-round">
                    <Icon name="camera" />
                  </span>
                  <b>Add photos</b>
                  <small>Up to 10 photos · JPG, PNG, or WEBP</small>
                </>
              )}
            </label>
            {photos.length > 0 && photos.length < 10 ? (
              <button
                type="button"
                className="text-button"
                onClick={() => imageInput.current?.click()}
              >
                Add more photos
              </button>
            ) : null}
          </div>

          <div className="form-grid">
            <label className="field">
              <span>What are you selling?</span>
              <input
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="e.g. Mid-century desk"
              />
            </label>
            <label className="field">
              <span>Price</span>
              <input
                disabled={form.free}
                type="number"
                min="0"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                placeholder="$0"
              />
            </label>
            <label className="field">
              <span>Category</span>
              <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                {CATEGORIES.slice(1).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Condition</span>
              <select value={form.condition} onChange={(e) => set('condition', e.target.value)}>
                {CONDITIONS.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Quantity</span>
              <input
                type="number"
                min="1"
                value={form.quantity}
                onChange={(e) => set('quantity', e.target.value)}
              />
            </label>
          </div>

          <label className="check-row">
            <input
              type="checkbox"
              checked={form.free}
              onChange={(e) => set('free', e.target.checked)}
            />
            <span>
              <b>This item is free</b>
              <small>Make someone’s day.</small>
            </span>
          </label>

          <label className="field">
            <span>Description</span>
            <textarea
              rows={5}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Share the important details, condition, and why you’re selling."
            />
          </label>

          <label className="field">
            <span>Pickup method</span>
            <select
              value={form.pickupMethod}
              onChange={(e) => set('pickupMethod', e.target.value)}
            >
              {PICKUP_METHODS.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="location-row-button"
            onClick={() => setShowLocation(true)}
          >
            <Icon name="mapPin" size={18} />
            <span>
              <b>{pickup?.label || 'Set pickup area'}</b>
              <small>City-level location only</small>
            </span>
            <Icon name="chevron" size={18} />
          </button>

          <label className="check-row">
            <input
              type="checkbox"
              checked={form.shipping}
              onChange={(e) => set('shipping', e.target.checked)}
            />
            <span>
              <b>Offer shipping</b>
              <small>Buyers can arrange delivery too.</small>
            </span>
          </label>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button
              type="button"
              className="secondary-button"
              disabled={busy}
              onClick={(e) => submit(e, true)}
            >
              {editing ? 'Save as draft' : 'Save draft'}
            </button>
            <button className="primary-button" disabled={busy}>
              {busy
                ? editing
                  ? 'Saving…'
                  : 'Creating…'
                : editing
                  ? 'Save changes'
                  : 'Submit for review'}
            </button>
          </div>
        </form>

        <LocationPicker
          open={showLocation}
          onClose={() => setShowLocation(false)}
          initial={pickup}
          showRadius={false}
          title="Pickup area"
          confirmLabel="Use this area"
          onConfirm={setPickup}
        />
      </section>
    );
  }

  return (
    <section className="content-page sell-page sell-hub">
      <div className="page-title sell-hub-head">
        <div>
          <h1>Selling</h1>
          <p>Manage your listings and see how they’re doing.</p>
        </div>
        <button
          type="button"
          className="primary-button add-listing-desktop"
          onClick={() => nav('/sell/new')}
        >
          <Icon name="plus" size={18} />
          Add listing
        </button>
      </div>

      <div className="sell-stats">
        <div>
          <b>{stats.totalViews}</b>
          <span>Views</span>
        </div>
        <div>
          <b>{stats.totalLikes}</b>
          <span>Likes</span>
        </div>
        <div>
          <b>{stats.count}</b>
          <span>Listings</span>
        </div>
      </div>

      {mine.length ? (
        <>
          <h2 className="sell-section-title">Your listings</h2>
          <div className="my-listings">
            {mine.map((item) => (
              <div key={item.id} className="my-listing-row">
                <Link to={`/listing/${item.id}`} className="my-listing-main">
                  <div className="conversation-avatar">
                    {coverPhoto(item) ? (
                      <img src={coverPhoto(item)} alt="" />
                    ) : (
                      <Icon name="camera" />
                    )}
                  </div>
                  <div>
                    <b>{item.title}</b>
                    <small>
                      {formatPrice(item)} · {listingStatusLabel(item.status)}
                    </small>
                  </div>
                </Link>
                <span className={`status-chip status-${item.status}`}>
                  {listingStatusLabel(item.status)}
                </span>
                <button
                  type="button"
                  className="icon-button outlined"
                  aria-label="Edit listing"
                  title="Edit"
                  onClick={() => nav(`/sell/edit/${item.id}`)}
                >
                  <Icon name="edit" size={16} />
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <Empty
          icon="tag"
          title="Start selling"
          body="List something you’re done with. It goes live after a quick review."
        />
      )}

      <button
        type="button"
        className="add-listing-fab"
        onClick={() => nav('/sell/new')}
      >
        <Icon name="plus" size={18} />
        Add listing
      </button>

      {toast ? <div className="toast">{toast}</div> : null}
    </section>
  );
}
